// utils/moorings.js — shared helpers for picking and labelling the buoys
// (moorings) that belong to a grid.

/** A buoy is "active" if it's still moored and has synced at some point this year. */
export const isActiveMooring = (m) => {
  const sync = m?.status?.latest_sync;
  return m?.unmoored_at == null && !!sync && new Date(sync).getFullYear() === new Date().getFullYear();
};

/**
 * Numbers a grid's buoys oldest→newest (by deployment date, then id) so labels
 * read "Buoy 1/2/3" consistently wherever they're rendered, regardless of the
 * order the API returns them in or the order they're displayed in.
 *
 * @param {Array} moorings
 * @returns {Object} mooring id → 1-based buoy number
 */
export const buoyNumbersById = (moorings) => {
  const out = {};
  [...(moorings || [])]
    .sort((a, b) => {
      const am = a?.moored_at ? new Date(a.moored_at).getTime() : null;
      const bm = b?.moored_at ? new Date(b.moored_at).getTime() : null;
      if (am != null && bm != null && am !== bm) return am - bm;
      return (a?.id ?? 0) - (b?.id ?? 0);
    })
    .forEach((m, i) => { out[m.id] = i + 1; });
  return out;
};

/**
 * A buoy's display label. In demo mode the real buoy name is never shown — the
 * positional number is used instead.
 */
export const buoyLabel = (mooring, number, demoMode = false) => {
  const fallback = `Buoy ${number ?? mooring?.id ?? ''}`.trim();
  if (demoMode) return fallback;
  return mooring?.buoy || fallback;
};

/**
 * Resolves which buoy a grid view should show: the explicitly requested one when
 * it belongs to the grid, otherwise the first active buoy, otherwise the first
 * buoy at all. Requested ids arrive from the URL, so they're compared as strings.
 *
 * @param {Array} moorings
 * @param {string|number|null} requestedId
 * @returns {Object|null}
 */
export const pickMooring = (moorings, requestedId) => {
  if (!moorings?.length) return null;
  if (requestedId != null && requestedId !== '') {
    const hit = moorings.find(m => String(m.id) === String(requestedId));
    if (hit) return hit;
  }
  return moorings.find(isActiveMooring) || moorings[0];
};
